import { useState, useEffect } from 'react';
import { Box, Text, Checkbox, TextInput, Button, Group, Alert, Divider, ActionIcon, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ChecklistItemDto, Task } from '../../types/checklist-types';
import { checklistItemService } from '../../services/checklistItemService';

interface ChecklistManagerProps {
  taskId: number;
  onProgressUpdate?: (progress: number) => void;
}

export default function ChecklistManager({ taskId, onProgressUpdate }: ChecklistManagerProps) {
  const [checklistItems, setChecklistItems] = useState<ChecklistItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [newItemText, setNewItemText] = useState('');

  useEffect(() => {
    loadChecklistItems();
  }, [taskId]);

  const loadChecklistItems = async () => {
    try {
      setLoading(true);
      const items = await checklistItemService.getByTaskId(taskId);
      setChecklistItems(items);
    } catch (error) {
      console.error('Error loading checklist items:', error);
      notifications.show({
        title: 'Errore',
        message: 'Errore nel caricamento della checklist',
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = async (itemId: number, checked: boolean) => {
    try {
      // Trova l'item corrente per mantenere gli altri valori
      const currentItem = checklistItems.find(item => item.id === itemId);
      if (!currentItem) return;

      const updateRequest = { 
        title: currentItem.title,
        order: currentItem.order,
        isCompleted: checked,
        description: currentItem.description
      };
      
      await checklistItemService.update(itemId, updateRequest);
      setChecklistItems(prev => 
        prev.map(item => 
          item.id === itemId ? { ...item, isCompleted: checked } : item
        )
      );
      
      // Notifica aggiornamento parent
      if (onProgressUpdate) {
        const updatedProgress = calculateProgress();
        onProgressUpdate(updatedProgress);
      }

      notifications.show({
        title: 'Checklist aggiornata',
        message: `Item ${checked ? 'completato' : 'rimosso da completati'}`,
        color: 'green'
      });
    } catch (error) {
      console.error('Error toggling item:', error);
      notifications.show({
        title: 'Errore',
        message: 'Errore nell\'aggiornamento dell\'item',
        color: 'red'
      });
    }
  };

  const handleAddItem = async () => {
    if (!newItemText.trim()) return;

    try {
      const createRequest = {
        title: newItemText.trim(),
        order: checklistItems.length,
        isCompleted: false,
        taskId: taskId
      };

      const createdItem = await checklistItemService.create(taskId, createRequest);
      setChecklistItems(prev => [...prev, createdItem]);
      setNewItemText('');

      notifications.show({
        title: 'Item aggiunto',
        message: 'Nuovo item aggiunto alla checklist',
        color: 'green'
      });
    } catch (error) {
      console.error('Error adding item:', error);
      notifications.show({
        title: 'Errore',
        message: 'Errore nell\'aggiunta dell\'item',
        color: 'red'
      });
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    try {
      await checklistItemService.delete(itemId);
      setChecklistItems(prev => prev.filter(item => item.id !== itemId));

      notifications.show({
        title: 'Item eliminato',
        message: 'Item rimosso dalla checklist',
        color: 'green'
      });
    } catch (error) {
      console.error('Error deleting item:', error);
      notifications.show({
        title: 'Errore',
        message: 'Errore nell\'eliminazione dell\'item',
        color: 'red'
      });
    }
  };

  const calculateProgress = () => {
    if (checklistItems.length === 0) return 0;
    const completed = checklistItems.filter(item => item.isCompleted).length;
    return Math.round((completed / checklistItems.length) * 100);
  };

  if (loading) {
    return <Text>Caricamento checklist...</Text>;
  }

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text fw={500}>Checklist ({checklistItems.filter(i => i.isCompleted).length}/{checklistItems.length})</Text>
        <Text size="sm" c="dimmed">{calculateProgress()}% completato</Text>
      </Group>

      {checklistItems.length === 0 && (
        <Alert mb="md">
          <Text size="sm">Nessun item nella checklist. Aggiungi il primo item qui sotto.</Text>
        </Alert>
      )}

      {checklistItems.map((item) => (
        <Group key={item.id} mb="xs" wrap="nowrap">
          <Checkbox
            checked={item.isCompleted}
            onChange={(event) => handleToggleItem(item.id, event.currentTarget.checked)}
          />
          <Text 
            style={{ 
              textDecoration: item.isCompleted ? 'line-through' : 'none',
              opacity: item.isCompleted ? 0.7 : 1,
              flex: 1
            }}
          >
            {item.title}
          </Text>
          <Tooltip label="Elimina item">
            <ActionIcon 
              size="sm" 
              color="red" 
              variant="subtle"
              onClick={() => handleDeleteItem(item.id)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ))}

      <Divider my="md" />

      <Group>
        <TextInput
          placeholder="Aggiungi nuovo item..."
          value={newItemText}
          onChange={(event) => setNewItemText(event.currentTarget.value)}
          style={{ flex: 1 }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleAddItem();
            }
          }}
        />
        <Button 
          leftSection={<IconPlus size={16} />}
          onClick={handleAddItem}
          disabled={!newItemText.trim()}
        >
          Aggiungi
        </Button>
      </Group>
    </Box>
  );
}